import React, {useEffect, useState} from "react";
import {Button} from "../components";

import {RadioGroup, RadioGroupItem} from "../components";
import {useMedia} from "../hooks/useMedia";
import * as Icon from "../icons";

import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

import {Banner} from "./Banner";
import {BenefitCards} from "./BenefitCards";
import {CallToActionBlock} from "./CallToActionBlock";
import {Hero} from "./Hero";
import {Ingredients} from "./Ingredients";
import {ModalDesktop} from "./ModalDesktop";
import {ModalMobile} from "./ModalMobile";
import {PrepSteps} from "./PrepSteps";
import {Recommendations} from "./Recommendations";
import {Testimonial} from "./Testimonial";
import {goToCheckoutViaCart, hasSellingPlans} from "../lib/utils";

dayjs.extend(duration);

const ProductLayout = ({
  productJson,
  demovideoUrl,
  testimonialPortraitUrl,
}: {
  productJson: RootObject;
  demovideoUrl: string;
  testimonialPortraitUrl: string;
}) => {
  const hasSteps =
    productJson.metafields.steps &&
    productJson.metafields.steps.length > 0 &&
    productJson.metafields.steps[0].text;
  const isSubscription = hasSellingPlans(productJson);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const isMobile = useMedia("(max-width: 768px)", true);
  const [people, setPeople] = useState<2 | 4>(2);

  const [selectedVariant, setSelectedVariant] = useState(
    productJson.variants[0]
  );

  const [formData, setFormData] = useState<ProductForm>({
    id: selectedVariant.id,
    available: selectedVariant.available,
    quantity: 1,
    errors: [],
    ...(productJson.gift_card && {
      "properties[__shopify_send_gift_card_to_recipient]": "",
      "properties[Recipient email]": "",
      "properties[Recipient name]": "",
      "properties[Message]": "",
      "properties[Send on]": "",
    }),
  });

  useEffect(() => {
    setFormData({
      ...formData,
      id: selectedVariant.id,
      available: selectedVariant.available,
    });
  }, [selectedVariant]);

  const handleFormSubmit = (params: Params) => {
    let {errors, available, ...restFormData} = formData;
    if (!restFormData["properties[__shopify_send_gift_card_to_recipient]"]) {
      restFormData = {
        id: restFormData.id,
        quantity: restFormData.quantity,
      };
    }
    goToCheckoutViaCart([restFormData], params);
  };

  let formRef: React.RefObject<HTMLFormElement> | null = null;

  const getFormRef = (ref: React.RefObject<HTMLFormElement>) => {
    formRef = ref;
  };

  const handleSubmit = () => {
    if (formRef && formRef.current) {
      formRef.current.click();
    }
  };

  return (
    <>
      <Hero
        productJson={productJson}
        selectedVariant={selectedVariant}
        getFormRef={getFormRef}
        setSelectedVariant={setSelectedVariant}
        formData={formData}
        setFormData={setFormData}
        handleFormSubmit={handleFormSubmit}
      />
      <div className="w-full max-w-7xl mx-auto md:flex md:items-start gap-4 py-2 md:py-8 md:px-4">
        <div className="flex flex-col md:w-[30rem] gap-4">
          {productJson.id &&
            productJson.ingredient_handles &&
            productJson.ingredient_handles.length > 0 && (
              <Ingredients
                people={people}
                setPeople={setPeople}
                ingredientHandles={productJson.ingredient_handles}
                productId={productJson.id}
              />
            )}
          <Banner
            pause={modalIsOpen}
            demovideoUrl={demovideoUrl}
            onClick={() => setModalIsOpen(true)}
          />
          <Testimonial testimonialPortraitUrl={testimonialPortraitUrl} />
        </div>
        <div className="md:w-[50rem] px-2 md:p-6 bg-white rounded-3xl flex flex-col gap-3">
          {hasSteps && <PrepSteps steps={productJson.metafields.steps} />}
          {!hasSteps && (
            <Recommendations
              recommendationUrl={productJson.recommendationUrl}
              displayGrid={true}
              limit={8}
            />
          )}
        </div>
      </div>
      <CallToActionBlock
        onAction={isSubscription ? () => setModalIsOpen(true) : handleSubmit}
        productJson={productJson}
        selectedVariant={selectedVariant}
      />
      {hasSteps && (
        <div className="bg-white py-8 md:p-20">
          <Recommendations recommendationUrl={productJson.recommendationUrl} />
        </div>
      )}
      {isMobile && (
        <ModalMobile
          title="Clean Kitchen teeb elu lihtsamaks"
          open={modalIsOpen}
          onClose={() => setModalIsOpen(false)}
        >
          <div className="overflow-x-scroll px-4">
            <BenefitCards />
          </div>
          <div className="flex gap-1 px-2">
            <div className="flex-1 border border-slate-100 rounded-xl p-3 flex items-start justify-center gap-2 [&>svg]:w-12">
              <Icon.Smile />
              <span className="leading-4 text-sm">
                Mitmele inimesele soovid tellida?
              </span>
            </div>
            <RadioGroup
              className="flex-1"
              defaultValue={people.toString()}
              onValueChange={(val) => setPeople(parseInt(val) as typeof people)}
            >
              <RadioGroupItem value="2" />
              <RadioGroupItem value="4" />
            </RadioGroup>
          </div>
          <Button className="mx-2 h-16" asChild>
            <a
              href={`/collections/boxes?productId=${productJson.id}&people=${people}`}
            >
              Vaata retsepte
            </a>
          </Button>
        </ModalMobile>
      )}
      {!isMobile && (
        <ModalDesktop
          open={modalIsOpen}
          onClose={() => setModalIsOpen(false)}
          productJson={productJson}
        />
      )}
    </>
  );
};

export {ProductLayout};
